import { useState } from 'react';
import type { Order, OrderStatus } from '@cracked-mug/shared';
import { OrderTicket } from './OrderTicket';

interface KitchenBoardProps {
  orders: Order[];
  onAdvance: (order: Order, next: OrderStatus) => Promise<void>;
  onCancel: (order: Order) => Promise<void>;
}

/**
 * Two-column layout matched to the single-tap workflow:
 *   - "Now"   → everything actively waiting on the barista (pending /
 *                accepted / preparing). One Done tap clears it.
 *   - "Done"  → recently completed (kept on screen for ~1 hr so staff
 *                can see what they just did).
 */
const ACTIVE_STATUSES: OrderStatus[] = ['pending', 'accepted', 'preparing', 'ready'];
const DONE_STATUSES: OrderStatus[] = ['completed'];

export function KitchenBoard({ orders, onAdvance, onCancel }: KitchenBoardProps) {
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set());

  const guard = async (orderId: string, op: () => Promise<void>): Promise<void> => {
    setPendingIds((prev) => new Set(prev).add(orderId));
    try {
      await op();
    } finally {
      setPendingIds((prev) => {
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
    }
  };

  const activeOrders = orders
    .filter((o) => ACTIVE_STATUSES.includes(o.status))
    .sort((a, b) => new Date(a.placedAt).getTime() - new Date(b.placedAt).getTime());
  const doneOrders = orders
    .filter((o) => DONE_STATUSES.includes(o.status))
    .sort((a, b) => {
      const ta = new Date(a.completedAt ?? a.placedAt).getTime();
      const tb = new Date(b.completedAt ?? b.placedAt).getTime();
      return tb - ta;
    });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 px-6 pb-8">
      <section className="lg:col-span-2" aria-labelledby="col-now">
        <h2
          id="col-now"
          className="font-display text-2xl text-wca-black mb-3 flex items-baseline justify-between"
        >
          <span>Now</span>
          <span className="rounded-full bg-wca-black/[0.05] px-2.5 py-0.5 font-mono text-sm text-wca-gray-mid tabular-nums">
            {activeOrders.length}
          </span>
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {activeOrders.length === 0 ? (
            <div className="text-wca-gray-mid font-mono text-xs uppercase tracking-widest text-center py-10 border border-dashed border-wca-gray-light rounded-2xl sm:col-span-2">
              No active orders
            </div>
          ) : (
            activeOrders.map((order) => (
              <OrderTicket
                key={order.orderId}
                order={order}
                onAdvance={(o, next) => guard(o.orderId, () => onAdvance(o, next))}
                onCancel={(o) => guard(o.orderId, () => onCancel(o))}
                isPending={pendingIds.has(order.orderId)}
              />
            ))
          )}
        </div>
      </section>

      <section aria-labelledby="col-done">
        <h2
          id="col-done"
          className="font-display text-2xl text-wca-black mb-3 flex items-baseline justify-between"
        >
          <span>Done</span>
          <span className="rounded-full bg-wca-black/[0.05] px-2.5 py-0.5 font-mono text-sm text-wca-gray-mid tabular-nums">
            {doneOrders.length}
          </span>
        </h2>
        <div className="space-y-3 opacity-75">
          {doneOrders.length === 0 ? (
            <div className="text-wca-gray-mid font-mono text-xs uppercase tracking-widest text-center py-10 border border-dashed border-wca-gray-light rounded-2xl">
              Nothing yet
            </div>
          ) : (
            doneOrders.map((order) => (
              <OrderTicket
                key={order.orderId}
                order={order}
                onAdvance={(o, next) => guard(o.orderId, () => onAdvance(o, next))}
                onCancel={(o) => guard(o.orderId, () => onCancel(o))}
                isPending={pendingIds.has(order.orderId)}
              />
            ))
          )}
        </div>
      </section>
    </div>
  );
}
